import { useCallback, useMemo } from "react";
import {
	UploadBatchFileHandlers,
	UploaderItemResult,
	UploadFileInfo
} from "../DocumentUploader/useDocumentUploader";
import { FolderNavigationStackItem } from "./useFolderNavigationStack";
import { UploadResult } from "client/Api/Documents/common";
import { FileUploadModel, uploadFile } from "client/Api/Documents/WebDav/webDavFileContent";
import { FileSystemItemModel, WebDavServerModel } from "client/Api/Documents/WebDav/webDav";
import {
	getOpenActions,
	OpenActionHandlers,
	WebDavDocumentAction,
	WebDavDocumentActionContext
} from "./actions";
import { reduceToAvailableActions } from "client_legacy/ndaw/actions";
import {
	WebDavEvent,
	publish
} from "client_legacy/Documents/WebDav/events";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { HttpRequestError } from "client/Api/httpClient";
import { localize } from "client_legacy/ndaw/culture";
import { UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { useEditorPreference } from "../useEditorPreference";
import { WebDavConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { AlertManager, useAlerts } from "client/Hooks";

export function useWebDavDocumentUploaderHandlers(
	server: WebDavServerModel,
	currentFolder: FolderNavigationStackItem | undefined,
	webDavConfiguration: WebDavConfiguration,
	onOpenFolder: (item: FolderNavigationStackItem) => void
): UploadBatchFileHandlers | undefined {
	const alertManager: AlertManager = useAlerts();

	const editorPreference: UserEditorPreference = useEditorPreference();

	const getOpenFileAction = useCallback(
		(
			uploadedFile: FileSystemItemModel,
			folder: FolderNavigationStackItem
		): (() => void) | undefined => {
			const context: WebDavDocumentActionContext = {
				server,
				folder,
				selectedDocuments: [uploadedFile],

				// Default values for properties that
				// we don't need in the uploader
				isInSelectMode: false,
				isMobile: false
			};

			const handlers: OpenActionHandlers = {
				openFolder: onOpenFolder,
				selectItem: (): void => {
					/** Nope */
				}
			};

			const actions: readonly WebDavDocumentAction[] = getOpenActions(
				handlers,
				[uploadedFile],
				webDavConfiguration,
				alertManager,
				editorPreference
			);

			const result: WebDavDocumentAction | undefined = reduceToAvailableActions(actions, context)[0];

			return result
				? (): void => result.execute(context)
				: undefined;
		},
		[
			alertManager,
			editorPreference,
			server,
			webDavConfiguration,
			onOpenFolder
		]
	);

	const executeFileUpload = useCallback(
		async (
			data: UploadFileInfo,
			folder: FolderNavigationStackItem,
			signal: AbortSignal
		): Promise<UploaderItemResult> => {
			try {
				const result: UploadResult = await uploadFile(
					server.id,
					folder.id,
					convertToModel(data),
					signal
				);

				if (result.createdTopFolder) {
					publish(
						WebDavEvent.CreatedOrReplaced,
						{
							serverId: server.id,
							item: result.createdTopFolder
						}
					);
				}

				if (result.success) {
					const {
						uploadedFile,
						uploadedFileName
					} = result;

					publish(
						WebDavEvent.CreatedOrReplaced,
						{
							serverId: server.id,
							item: uploadedFile
						}
					);

					const openFile: (() => void) | undefined = getOpenFileAction(
						uploadedFile,
						// The actual file folder isn't needed for opening operations, so we use the current folder
						folder
					);

					return {
						kind: "success",
						uploadedFileName,
						uploadedFileActions: {
							openFile
						}
					};
				}

				if (result.isNamingConflict) {
					return {
						kind: "name-conflict",
						message: result.error
					};
				}

				return {
					kind: "failed",
					message: result.error
				};
			} catch (e) {
				if (isCancelledError(e)) {
					return { kind: "failed" };
				}

				let message: string | undefined;
				if (e instanceof HttpRequestError) {
					switch (e.statusCode) {
						case 413:
							message = localize("_FileSizeTooLarge");
							break;
						default:
							break;
					}
				}

				return {
					kind: "failed",
					message
				};
			}
		},
		[getOpenFileAction, server.id]
	);

	return useMemo(
		(): UploadBatchFileHandlers | undefined => currentFolder
			? {
				uploadFile: (data, signal) => executeFileUpload(
					data,
					currentFolder,
					signal
				)
			}
			: undefined,
		[
			currentFolder,
			executeFileUpload
		]
	);
}

function convertToModel(seedData: UploadFileInfo): FileUploadModel {
	const {
		nameConflictResolution,
		file,
		fileName,
		path
	} = seedData;

	return {
		file,
		fileName,
		path,
		nameConflictResolution
	};
}